import React from 'react'
import {
  Image,
  ImageSourcePropType,
  ImageStyle,
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { InlineAlert } from '@src/components'
import { useIsDarkMode } from '@src/config/theme'
import { StyleService, useStyleSheet } from '@src/style/service'
import { UiInlineAlert } from '@src/types'
import { Button, Icon, Radio, Text } from '@components/base'

export interface OfferOption {
  id: string
  title: string
  description: string
  price?: string
  priceSuffix?: string
  badge?: string
}

interface OfferContentProps {
  title: string
  description?: string
  benefits: readonly string[]
  options: readonly OfferOption[]
  selectedOptionId: string
  onOptionPress: (optionId: string) => void
  note?: string
  primaryActionText: string
  secondaryActionText: string
  onPrimaryActionPress: () => void
  onSecondaryActionPress: () => void
  primaryActionDisabled?: boolean
  showCloseButton?: boolean
  onClosePress?: () => void
  closeButtonStyle?: StyleProp<ViewStyle>
  showPrimaryActionArrow?: boolean
  // Optional hero image at the top of the screen. Omit both to render
  // the offer without an image (the title slides up to fill the space).
  imageSource?: ImageSourcePropType
  imageSourceDark?: ImageSourcePropType
  // When true, the title sits closer to the hero image. Useful when the
  // offer has more body content (benefits + many options) and needs the
  // extra vertical room.
  compactTitle?: boolean
}

export const OfferContent = ({
  title,
  description,
  benefits,
  options,
  selectedOptionId,
  onOptionPress,
  note,
  primaryActionText,
  secondaryActionText,
  onPrimaryActionPress,
  onSecondaryActionPress,
  primaryActionDisabled,
  showCloseButton = false,
  onClosePress,
  closeButtonStyle,
  showPrimaryActionArrow = true,
  imageSource,
  imageSourceDark,
  compactTitle = false,
}: OfferContentProps) => {
  const styles = useStyleSheet(themedStyles)
  const insets = useSafeAreaInsets()
  const isDarkMode = useIsDarkMode()
  const resolvedImageSource = isDarkMode
    ? imageSourceDark ?? imageSource
    : imageSource ?? imageSourceDark
  const hasImage = !!resolvedImageSource

  let titleStyle = styles.titleNoImage
  if (hasImage) {
    titleStyle = compactTitle ? styles.titleCompact : styles.title
  }

  return (
    <View style={styles.container}>
      <ScrollView
        bounces={false}
        contentContainerStyle={[styles.content, { paddingTop: insets.top }]}
        showsVerticalScrollIndicator={false}
      >
        {hasImage && (
          <View style={styles.imageContainer}>
            <Image source={resolvedImageSource} style={styles.image as ImageStyle} />
          </View>
        )}

        {showCloseButton && !!onClosePress && (
          <TouchableOpacity
            activeOpacity={0.8}
            style={[styles.closeButton, closeButtonStyle]}
            onPress={onClosePress}
            accessibilityLabel="close"
          >
            <Icon name="x" weight="bold" style={styles.closeButtonIcon} />
          </TouchableOpacity>
        )}

        <View style={styles.textContainer}>
          <Text type="title-1" style={titleStyle}>
            {title}
          </Text>
          {!!description && (
            <Text type="large" style={styles.description}>
              {description}
            </Text>
          )}
        </View>

        {benefits.length > 0 && (
          <View style={styles.benefits}>
            {benefits.map((benefit) => (
              <View style={styles.benefitContainer} key={benefit}>
                <Icon size={20} weight="fill" name="check-circle" style={styles.checkmarkIcon} />
                <Text type="large" style={styles.benefitText}>
                  {benefit}
                </Text>
              </View>
            ))}
          </View>
        )}

        <View style={styles.options}>
          {options.map((option) => {
            const selected = option.id === selectedOptionId
            const onPress = () => onOptionPress(option.id)

            return (
              <TouchableOpacity
                accessibilityLabel={option.title}
                style={[styles.optionCard, selected && styles.optionCardSelected]}
                onPress={onPress}
                key={option.id}
                activeOpacity={0.75}
              >
                {!!option.badge && (
                  <View style={styles.optionBadge}>
                    <Text type="regular" bold style={styles.optionBadgeText}>
                      {option.badge}
                    </Text>
                  </View>
                )}

                <View style={styles.optionRow}>
                  <View style={styles.optionMain}>
                    <Radio checked={selected} onPress={onPress} />
                    <View style={styles.optionTextContainer}>
                      <Text type="title-3">{option.title}</Text>
                      <Text type="regular" style={styles.optionDescription}>
                        {option.description}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.optionPriceContainer}>
                    {!!option.price && (
                      <View style={styles.optionPriceTextContainer}>
                        <Text type="large" bold>
                          {option.price}
                        </Text>
                        {!!option.priceSuffix && (
                          <Text type="large" bold>
                            {option.priceSuffix}
                          </Text>
                        )}
                      </View>
                    )}
                  </View>
                </View>
              </TouchableOpacity>
            )
          })}
        </View>

        {!!note && (
          <InlineAlert style={styles.noteAlert} category={UiInlineAlert.Neutral} message={note} />
        )}
      </ScrollView>

      <View style={[styles.actionsContainer, { paddingBottom: Math.max(insets.bottom, 16) }]}>
        <Button
          type="primary"
          size="block"
          onPress={onPrimaryActionPress}
          disabled={primaryActionDisabled}
          accessibilityLabel={primaryActionText}
          accessoryRight={
            showPrimaryActionArrow ? <Icon name="arrow-right" weight="bold" /> : undefined
          }
        >
          {primaryActionText}
        </Button>
        <Button
          type="transparent"
          size="block"
          onPress={onSecondaryActionPress}
          accessibilityLabel={secondaryActionText}
        >
          {secondaryActionText}
        </Button>
      </View>
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
  },
  imageContainer: {
    width: '100%',
    position: 'absolute',
  },
  image: {
    width: '100%',
    height: 'auto',
    aspectRatio: 1179 / 1158,
    resizeMode: 'contain',
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  closeButton: {
    marginTop: 16,
    marginLeft: 16,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'theme.surface.base1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonIcon: {
    width: 16,
    height: 16,
    color: 'theme.solid.white',
  },
  textContainer: {
    marginHorizontal: 16,
  },
  title: {
    marginTop: 132,
    maxWidth: 324,
  },
  titleCompact: {
    marginTop: 96,
    maxWidth: 324,
  },
  titleNoImage: {
    marginTop: 24,
    maxWidth: 324,
  },
  description: {
    marginTop: 8,
    maxWidth: 340,
  },
  benefits: {
    marginTop: 16,
    marginBottom: 20,
    marginHorizontal: 16,
    gap: 12,
  },
  benefitContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  checkmarkIcon: {
    color: 'theme.success.base',
  },
  benefitText: {
    flex: 1,
  },
  options: {
    marginTop: 12,
    marginBottom: 24,
    marginHorizontal: 16,
    gap: 8,
  },
  optionCard: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'theme.surface.base1',
    paddingVertical: 16,
    paddingHorizontal: 14,
    backgroundColor: 'theme.background',
  },
  optionCardSelected: {
    borderColor: 'theme.primary.base',
    borderWidth: 1,
    backgroundColor: 'theme.surface.base2',
  },
  optionBadge: {
    position: 'absolute',
    top: -14,
    alignSelf: 'center',
    borderRadius: 8,
    backgroundColor: 'theme.success.base',
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  optionBadgeText: {
    color: 'theme.solid.white',
  },
  optionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  optionMain: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    gap: 8,
  },
  optionTextContainer: {
    flexShrink: 1,
    marginLeft: 4,
  },
  optionDescription: {
    color: 'theme.text.secondary',
  },
  optionPriceContainer: {
    alignItems: 'flex-end',
    marginLeft: 12,
    gap: 8,
  },
  optionPriceTextContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  noteAlert: {
    marginHorizontal: 16,
    marginBottom: 24,
  },
  actionsContainer: {
    paddingHorizontal: 16,
    gap: 8,
  },
})
